import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  CloudArrowUpIcon,
  MusicalNoteIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface AudioUploadZoneProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  isUploading?: boolean;
  uploadProgress?: number;
}

const AudioUploadZone: React.FC<AudioUploadZoneProps> = ({
  onFileSelect,
  selectedFile,
  isUploading = false,
  uploadProgress,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) onFileSelect(file);
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.ogg', '.webm', '.flac', '.aac', '.mpeg', '.mpg', '.mpga', '.mp2'],
    },
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024, // 100MB
    disabled: isUploading,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (selectedFile) {
    return (
      <div className="border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0">
            <MusicalNoteIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatFileSize(selectedFile.size)}
              </p>
            </div>
          </div>

          {!isUploading && (
            <button
              type="button"
              onClick={() => onFileSelect(null)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {previewUrl && !isUploading && (
          <audio src={previewUrl} controls className="w-full mt-4 h-9" />
        )}

        {isUploading && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>
                {uploadProgress !== undefined && uploadProgress < 100
                  ? 'Uploading...'
                  : 'Starting translation...'}
              </span>
              {uploadProgress !== undefined && uploadProgress < 100 && (
                <span className="font-medium">{uploadProgress}%</span>
              )}
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-200"
                style={{
                  width: uploadProgress !== undefined && uploadProgress < 100
                    ? `${uploadProgress}%`
                    : '100%',
                  animation: uploadProgress === undefined || uploadProgress >= 100
                    ? 'pulse 1.5s ease-in-out infinite'
                    : 'none',
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
        isDragActive
          ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
      } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <input {...getInputProps()} />

      <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />

      <div className="mt-4">
        <p className="text-lg font-medium text-gray-900 dark:text-white">
          {isDragActive ? 'Drop audio here' : 'Upload audio file'}
        </p>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Drag and drop your audio file here, or click to browse
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Supports MP3, WAV, M4A, OGG, FLAC, AAC, MPEG (max 100MB)
        </p>
      </div>
    </div>
  );
};

export default AudioUploadZone;
